import React from 'react';
import { DNGatsbyTemplate } from '../DriveNinjaTemplate/gatsby';
import { LocaleProvider } from '../../../contexts/language';
import { useLanguageLink } from '../../hooks/useLanguageLink';
import { createIntl, FormattedMessage, IntlProvider } from 'react-intl';
import ar from './ar.json';
import en from './en.json';
import { graphql } from 'gatsby';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import { Button } from 'gatsby-theme-material-ui';
import Stack from '@mui/material/Stack';
import { GatsbyImage, getImage, StaticImage } from 'gatsby-plugin-image';
import { useTranslation } from '../../hooks/useTranslatedField';
import PropTypes from 'prop-types';
import ModelCardFavorite from './modelCardFavorite';
import useBuildSearch from '../../hooks/useBuildSearch';
import { useCarLink } from '../../hooks/useCarLink';

const languages = {
  ar, en
};


const ManufacturerPage = ({ pageContext: { lang, direction }, data }) => {
  const intl = createIntl({ locale: lang || 'en', messages: languages[lang] || languages.en });
  const l = useLanguageLink();
  const cl = useCarLink();

  const t = useTranslation();
  const s = useBuildSearch();

  const manufacturer = data?.driveNinjaManufacturerType;

  const useGatsbySortedImages = ({ modelImages, translatedName }) => {
    const elements = modelImages?.sort((a, b) => a.orderingPriority - b.orderingPriority).map(({ localImage }, idx) =>
      <GatsbyImage
        key={idx}
        image={getImage(localImage)}
        layout="fixed"
        alt={t(translatedName)}
        height={200}
        style={{ width: '100%' }}
      />);
    if (!elements?.length) {
      return [<StaticImage
        key={1}
        src={'./images/modelPlaceholder.png'}
        layout="fixed"
        alt={`${t(translatedName)} logo`}
        height={200}
        style={{ width: '100%' }}
      />];
    }
    return elements;
  };

  const offersForTrimLevels = ({ trimLevels }) => {
    const allTrimLevels = trimLevels.edges?.map(n => n.node);
    const allTrimLevelOffers = allTrimLevels.map(t => t.newOffers.edges).reduce((a, b) => [...a, ...b], []).map(({ node }) => node);
    const allOffers = allTrimLevelOffers.filter(x => x !== null).filter(({ available }) => available);
    return allOffers;
  };

  const models = manufacturer?.models?.edges?.map(n => n.node).map(({
    slug, translatedName, modelImages, trimLevels
  }) => {
    const o = offersForTrimLevels({ trimLevels }) || [];
    const { currency, monthlyAmount, purchaseAmount } = o.sort((a, b) => a.monthlyAmount - b.monthlyAmount)?.[0] || {};
    return {
      slug,
      images: useGatsbySortedImages({ modelImages, translatedName }),
      title: `${t(manufacturer.translatedName)} ${t(translatedName)}`,
      link: cl({ mfrSlug: manufacturer.slug, mdlSlug: slug }),
      linkText: intl.formatMessage({
        id: 'manufacturerPage.linkTitle', defaultMessage: 'View model details for {make} {model}'
      }, {
        make: t(manufacturer.translatedName), model: t(translatedName)
      }),
      stockAvailability: o.length ? o.sort((a, b) => a.stockAvailabilityRank - b.stockAvailabilityRank)?.[0]?.stockAvailability : 'NOT_AVAILABLE',
      monthlyPrice: monthlyAmount ? parseFloat(monthlyAmount) : undefined,
      purchasePrice: purchaseAmount ? parseFloat(purchaseAmount) : undefined,
      currency,
      term: currency ? 'Monthly' : undefined
    };
  });


  return (<LocaleProvider localeConfig={{ lang, direction }}>
    <DNGatsbyTemplate
      breadcrumbs={[{
        name: intl.formatMessage({
          id: 'browseMake.BrowseMakeBreadcrumbs', defaultMessage: 'Browse by Manufacturer'
        }), path: l('/make/')
      }, {
        name: t(manufacturer?.translatedName), path: cl({ mfrSlug: manufacturer.slug })
      }]}
    >
      <IntlProvider locale={lang} messages={languages[lang] || languages.en}>
        <Stack spacing={3} sx={{ mb: 2 }}>
          {manufacturer?.logoFile ? (<GatsbyImage
            image={getImage(manufacturer?.logoFile)}
            layout="fixed"
            alt={t(manufacturer?.translatedName)}
            height={200}
            style={{ maxWidth: '100%', alignSelf: 'center' }}
          />) : null}
          <Typography variant={'h1'}>
            <FormattedMessage
              id={'ManufacturerPage.title'} defaultMessage={'All {manufacturer} models'}
              values={{ manufacturer: t(manufacturer.translatedName) }}
            />
          </Typography>
          <Typography variant={'body'}>
            <FormattedMessage
              id={'ManufacturerPage.preIntro'}
              defaultMessage={'Scroll down to view all {manufacturer} models from our hand-picked suppliers.'}
              values={{ manufacturer: t(manufacturer.translatedName) }}
            />
          </Typography>
          <Typography variant={'body'}>
            <FormattedMessage
              id={'ManufacturerPage.intro'}
              defaultMessage={'Want to compare other brands, filter price, or select the features you want? Try our advanced search!'}
            />
          </Typography>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              sx={{ width: { xs: '100%', md: 'auto' } }}
              to={s({ mfr: manufacturer.slug })}
              title={intl.formatMessage({
                id: 'ManufacturerPage.advancedSearchTitle', defaultMessage: 'Advanced Search'
              })}
            >
              {intl.formatMessage({
                id: 'ManufacturerPage.advancedSearchTitle', defaultMessage: 'Advanced Search'
              })}
            </Button>
          </div>
          <Grid container spacing={3}>
            {models?.length ? models.sort((a, b) => {
              if (parseFloat(a.purchasePrice) && parseFloat(b.purchasePrice)) {
                return parseFloat(a.purchasePrice) - parseFloat(b.purchasePrice);
              }
              if (a.purchasePrice) {
                return -1;
              }
              if (b.purchasePrice) {
                return 1;
              }
              return a.title.localeCompare(b.title);
            }).map(model => (<Grid key={model?.slug} xs={12} md={6} lg={4} sx={{ display: 'flex' }}><ModelCardFavorite
              cardProps={{ sx: { width: '100%' } }} {...model} key={model.slug} /></Grid>)) : (
              <Typography variant={'body'}>
                <FormattedMessage
                  id={'ManufacturerPage.noVehicles'}
                  defaultMessage={'There are no {manufacturer} models available right now.'}
                  values={{ manufacturer: manufacturer.name }}
                />
              </Typography>)}
          </Grid>
        </Stack>


      </IntlProvider>
    </DNGatsbyTemplate>
  </LocaleProvider>);

};

export default ManufacturerPage;
//
export const Head = ({ pageContext, data }) => {
  const { lang } = pageContext;
  const intl = createIntl({ locale: lang || 'en', messages: languages[lang] || languages.en });
  const manufacturer = data.driveNinjaManufacturerType;
  const t = useTranslation();
  return (<>
    <meta
      name="description"
      content={intl.formatMessage({
        id: 'ManufacturerPage.meta.description',
        defaultMessage: 'Buy, Finance or Lease a new or used {manufacturer}.  Save upto 15% off the list price.  Find the best deal now!'
      }, {
        manufacturer: t(manufacturer.translatedName)
      })}
    />

    <meta
      name="og:description"
      content={intl.formatMessage({
        id: 'ManufacturerPage.meta.description',
        defaultMessage: 'Buy, Finance or Lease a new or used {manufacturer}.  Save upto 15% off the list price.  Find the best deal now!'
      }, {
        manufacturer: t(manufacturer.translatedName)
      })}
    />

    <meta
      name="og:title"
      content={intl.formatMessage({
        id: 'ManufacturerPage.meta.title', defaultMessage: 'All {manufacturer} models'
      }, {
        manufacturer: t(manufacturer.translatedName)
      })}

    />
    <meta
      name="og:image"
      content={manufacturer.logoFile.publicURL}
    />

    <title>{intl.formatMessage({
      id: 'ManufacturerPage.meta.title', defaultMessage: 'All {manufacturer} models'
    }, {
      manufacturer: t(manufacturer.translatedName)
    })}</title>
  </>);
};


export const query = graphql`query ($id: String!) {
    driveNinjaManufacturerType(id: {eq: $id}) {
        translatedName {
            en
            ar
        }
        logoFile{
            publicURL
            childImageSharp {
                gatsbyImageData(height: 200, layout: FIXED)
                id
            }

        }
        slug
        id
        models {
            edges {
                node {
                    translatedName {
                        en
                        ar
                    }
                    trimLevels {
                        edges {
                            node {
                                newOffers {
                                    edges {
                                        node{
                                            purchaseAmount
                                            currency
                                            monthlyAmount
                                            stockAvailability
                                            stockAvailabilityRank
                                            available
                                        }
                                    }
                                }
                            }
                        }
                    }

                    slug
                    modelImages{
                        orderingPriority
                        localImage {
                            childImageSharp {
                                gatsbyImageData(height: 200, layout: FIXED)
                                id
                            }
                        }
                    }
                }
            }
        }
    }
}`;


ManufacturerPage.propTypes = {
  pageContext: PropTypes.shape({
    lang: PropTypes.string.isRequired, direction: PropTypes.string.isRequired
  }), data: PropTypes.shape({
    allDriveNinjaPurchaseNewOfferType: PropTypes.shape({
      group: PropTypes.arrayOf(PropTypes.shape({
        fieldValue: PropTypes.string.isRequired, edges: PropTypes.arrayOf(PropTypes.shape({
          node: PropTypes.shape({
            purchaseAmount: PropTypes.string.isRequired,
            currency: PropTypes.string.isRequired,
            monthlyAmount: PropTypes.string.isRequired,
            stockAvailability: PropTypes.string.isRequired,
            stockAvailabilityRank: PropTypes.number.isRequired
          })
        }))
      }).isRequired)
    }), driveNinjaManufacturerType: PropTypes.shape({
      translatedName: PropTypes.shape({
        ar: PropTypes.string, en: PropTypes.string.isRequired
      }).isRequired, slug: PropTypes.string.isRequired, models: PropTypes.shape({
        edges: PropTypes.arrayOf(PropTypes.shape({
          node: PropTypes.shape({
            translatedName: PropTypes.shape({
              ar: PropTypes.string, en: PropTypes.string.isRequired
            }).isRequired, slug: PropTypes.string.isRequired, modelImages: PropTypes.arrayOf(PropTypes.shape({
              orderingPriority: PropTypes.number.isRequired, localImage: PropTypes.shape({})
            })).isRequired
          }).isRequired
        }).isRequired).isRequired
      }).isRequired
    })
  })
};

